/**
 * ============================================================================
 * Calculator V2 - Modern Interface
 * ============================================================================
 * 
 * Nouvelle interface Calculator avec:
 * - Navigation 3 niveaux (Catégories → FE → Saisie)
 * - Design enrichi (couleurs, icônes, badges, progression)
 * - Calcul temps réel
 * - Persistance base de données
 * - Indicateur de sauvegarde
 * - Comparatif benchmark
 * 
 * ============================================================================
 */

import { useAuth } from '@/hooks/useAuth';
import { useRealEmissions } from '@/hooks/useRealEmissions';
import { trpc } from '@/lib/trpc';
import { useEffect, useMemo, useState } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { CategoryCard } from '@/components/calculator/CategoryCard';
import { CategoryDetail } from '@/components/calculator/CategoryDetail';
import { EmissionDrawer, EmissionFactor } from '@/components/calculator/EmissionDrawer';
import { LastSaveIndicator } from '@/components/calculator/LastSaveIndicator';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getPeriodOptions } from '@/lib/periodOptions';
import { ArrowLeft, Calendar, Loader2, Plus, Lock, Unlock } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useTranslation } from 'react-i18next';

type ViewMode = 'categories' | 'category-detail';

export default function CalculatorV2() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState<string>('');
  const [viewMode, setViewMode] = useState<ViewMode>('categories');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedFactor, setSelectedFactor] = useState<EmissionFactor | null>(null);

  // Period creation state
  const [isCreatePeriodOpen, setIsCreatePeriodOpen] = useState(false);
  const [periodType, setPeriodType] = useState<'month' | 'quarter' | 'year'>('month');
  const [periodLabel, setPeriodLabel] = useState('');
  const [periodYear, setPeriodYear] = useState(new Date().getFullYear());

  // Fetch periods
  const { data: periodsData, isLoading: loadingPeriods, refetch: refetchPeriods } = trpc.periods.list.useQuery(undefined, {
    enabled: !!user,
  });
  const periods = periodsData || [];

  // Period mutations
  const createPeriodMutation = trpc.periods.create.useMutation({
    onSuccess: () => {
      toast.success(t('calculator.period_created_successfully'));
      refetchPeriods();
      setIsCreatePeriodOpen(false);
      setPeriodLabel('');
    },
    onError: (error) => {
      toast.error(`${t('errors.generic')}: ${error.message}`);
    },
  });

  const closePeriodMutation = trpc.periods.close.useMutation({
    onSuccess: () => {
      toast.success(t('calculator.period_closed_successfully'));
      refetchPeriods();
    },
    onError: (error) => {
      toast.error(`${t('errors.generic')}: ${error.message}`);
    },
  });

  const reopenPeriodMutation = trpc.periods.update.useMutation({
    onSuccess: () => {
      toast.success(t('calculator.period_reopened_successfully'));
      refetchPeriods();
    },
    onError: (error) => {
      toast.error(`Failed to reopen period: ${error.message}`);
    },
  });

  // Fetch emission factors
  const { data: factorsData, isLoading: loadingFactors } = trpc.calculator.getFactors.useQuery(undefined, {
    enabled: !!user,
  });
  const factors = factorsData || [];

  // Real emissions management
  const periodId = selectedPeriod || null;
  const {
    emissions,
    addEmission,
    getEmissionsByCategory,
    getCategoryMetrics,
    totalEmissions,
    isSubmitting,
  } = useRealEmissions(periodId);

  // Group factors by category
  const categorizedFactors = useMemo(() => {
    if (!factors.length) return {};
    
    return factors.reduce((acc, factor) => {
      const category = factor.category || 'Other';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(factor as EmissionFactor);
      return acc;
    }, {} as Record<string, EmissionFactor[]>);
  }, [factors]);

  // Get completed factors for selected category
  const completedFactorsMap = useMemo(() => {
    if (!selectedCategory) return new Map();
    
    const categoryEmissions = getEmissionsByCategory(selectedCategory);
    const map = new Map();
    
    categoryEmissions.forEach((emission) => {
      map.set(emission.fe_key, {
        activity_value: emission.activity_value,
        tco2e: emission.tco2e,
        description: emission.description,
      });
    });
    
    return map;
  }, [selectedCategory, emissions, getEmissionsByCategory]);

  // Auto-select first open period
  useEffect(() => {
    if (periods.length > 0 && !selectedPeriod) {
      const openPeriod = periods.find((p: any) => p.status === 'open');
      if (openPeriod) {
        setSelectedPeriod(openPeriod.id.toString());
      }
    }
  }, [periods, selectedPeriod]);

  // Selected period data
  const selectedPeriodData = periods.find((p: any) => p.id.toString() === selectedPeriod);

  // Handle category selection
  const handleSelectCategory = (category: string) => {
    setSelectedCategory(category);
    setViewMode('category-detail');
  };

  // Handle back to categories
  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setViewMode('categories');
  };

  // Handle factor selection
  const handleSelectFactor = (factor: EmissionFactor) => {
    // Find existing emission for this factor
    const existing = emissions.find((e: any) => e.fe_key === factor.fe_key);
    
    // Store factor with existing data if any
    setSelectedFactor({
      ...factor,
      existingValue: existing?.activity_value,
      existingDescription: existing?.notes,
    } as any);
  };

  // Handle drawer close
  const handleCloseDrawer = () => {
    setSelectedFactor(null);
  };

  // Handle emission submission
  const handleSubmitEmission = async (data: { value: number; description?: string }) => {
    if (!selectedFactor || !periodId) return;

    try {
      await addEmission({
        fe_key: selectedFactor.fe_key,
        category: selectedFactor.category,
        activity_value: data.value,
        factor_value: selectedFactor.factor_value || 0,
        unit: selectedFactor.unit || 'unit',
        description: data.description,
        periodId,
      });

      toast.success(t('calculator.emission_added_successfully'));
      handleCloseDrawer();
    } catch (error) {
      console.error('Failed to add emission:', error);
      toast.error(t('errors.generic'));
    }
  };

  // Loading state
  if (loadingPeriods || loadingFactors) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  // No factors available
  if (factors.length === 0) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto p-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('calculator.no_emission_factors_available')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                {t('calculator.contact_administrator')}
              </p>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h1 className="text-3xl font-bold">{t('calculator.carbon_calculator')}</h1>
            <p className="text-muted-foreground">
              {t('calculator.track_and_manage_your_emissions_by_period')}
            </p>
          </div>

          <div className="flex items-center gap-4">
            <LastSaveIndicator lastSaveTime={emissions[0]?.created_at} />
            
            <div className="flex items-center gap-2">
              <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                <SelectTrigger className="w-[200px]">
                  <Calendar className="h-4 w-4 mr-2" />
                  <SelectValue placeholder={t('calculator.select_a_period')} />
                </SelectTrigger>
                <SelectContent>
                  {periods.map((period: any) => (
                    <SelectItem key={period.id} value={period.id.toString()}>
                      {period.period_label}{' '}
                      <span className="text-xs text-muted-foreground">
                        ({period.status === 'closed' ? 'closed' : period.status})
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                onClick={() => setIsCreatePeriodOpen(true)}
                variant="outline"
                size="sm"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('calculator.create_new_period')}
              </Button>

              {/* Admin controls */}
              {selectedPeriod && user?.role && ['mypa_super_admin', 'mypa_data_admin'].includes(user.role) && (
                <div className="flex gap-2">
                  {selectedPeriodData?.status === 'closed' ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        if (confirm(`Reopen period "${selectedPeriodData.period_label}"?`)) {
                          reopenPeriodMutation.mutate({
                            periodId: selectedPeriod,
                            status: 'open',
                          });
                        }
                      }}
                      disabled={reopenPeriodMutation.isPending}
                    >
                      {reopenPeriodMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Unlock className="h-4 w-4" />
                      )}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        if (confirm(`Close period "${selectedPeriodData?.period_label}"? This will prevent any further emissions from being added.`)) {
                          closePeriodMutation.mutate({ periodId: selectedPeriod });
                        }
                      }}
                      disabled={closePeriodMutation.isPending}
                    >
                      {closePeriodMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Lock className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Summary Card */}
        {selectedPeriod && (
          <Card>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <div className="text-sm text-muted-foreground">{t('calculator.total_emissions')}</div>
                  <div className="text-3xl font-bold text-primary">
                    {totalEmissions.toFixed(4)} <span className="text-lg">tCO₂e</span>
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">{t('calculator.emissions_recorded')}</div>
                  <div className="text-3xl font-bold">
                    {emissions.length}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">{t('calculator.active_categories')}</div>
                  <div className="text-3xl font-bold">
                    {Object.keys(categorizedFactors).filter(cat => 
                      getEmissionsByCategory(cat).length > 0
                    ).length}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Content */}
        {!selectedPeriod ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Calendar className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">{t('calculator.select_a_period')}</h3>
              <p className="text-muted-foreground">
                {t('calculator.choose_period_to_start')}
              </p>
            </CardContent>
          </Card>
        ) : viewMode === 'categories' ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold">{t('calculator.add_activities_by_category')}</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(categorizedFactors).map(([category, categoryFactors]) => {
                const metrics = getCategoryMetrics(category, categoryFactors.length);
                const completedFactors = getEmissionsByCategory(category);

                return (
                  <CategoryCard
                    key={category}
                    category={category}
                    factorCount={categoryFactors.length}
                    completedCount={completedFactors.length}
                    totalCO2={metrics.total}
                    progress={metrics.progress}
                    benchmark={metrics.benchmark}
                    vsBenchmark={metrics.vsBenchmark}
                    onClick={() => handleSelectCategory(category)}
                  />
                );
              })}
            </div>
          </div>
        ) : viewMode === 'category-detail' && selectedCategory ? (
          <div className="space-y-4">
            <Button
              variant="outline"
              onClick={handleBackToCategories}
              className="mb-4 font-medium border-2 border-green-600 text-green-700 hover:bg-green-50 hover:text-green-800"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              {t('calculator.back_to_categories')}
            </Button>

            <CategoryDetail
              category={selectedCategory}
              factors={categorizedFactors[selectedCategory] || []}
              completedFactors={completedFactorsMap}
              onSelectFactor={handleSelectFactor}
              onBack={handleBackToCategories}
            />
          </div>
        ) : null}

        {/* Emission Drawer */}
        <EmissionDrawer
          open={!!selectedFactor}
          factor={selectedFactor}
          onClose={handleCloseDrawer}
          onSubmit={handleSubmitEmission}
          isSubmitting={isSubmitting}
          initialValue={(selectedFactor as any)?.existingValue}
          initialDescription={(selectedFactor as any)?.existingDescription}
        />

        {/* Create Period Dialog */}
        <Dialog open={isCreatePeriodOpen} onOpenChange={setIsCreatePeriodOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('calculator.create_new_period')}</DialogTitle>
              <DialogDescription>
                {t('calculator.create_reporting_period_description')}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>{t('calculator.period_type')}</Label>
                <Select
                  value={periodType}
                  onValueChange={(value) => setPeriodType(value as 'month' | 'quarter' | 'year')}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="month">{t('calculator.monthly')}</SelectItem>
                    <SelectItem value="quarter">{t('calculator.quarterly')}</SelectItem>
                    <SelectItem value="year">{t('calculator.yearly')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{t('calculator.period_label')}</Label>
                <Select value={periodLabel} onValueChange={setPeriodLabel}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('calculator.select_a_period')} />
                  </SelectTrigger>
                  <SelectContent>
                    {getPeriodOptions(periodType, periodYear).map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{t('calculator.year')}</Label>
                <Input
                  type="number"
                  value={periodYear}
                  onChange={(e) => setPeriodYear(parseInt(e.target.value))}
                  min={2000}
                  max={2100}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreatePeriodOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={() => {
                  if (!periodLabel) {
                    toast.error(t('calculator.please_select_a_period_label'));
                    return;
                  }
                  createPeriodMutation.mutate({
                    periodType,
                    periodLabel,
                    year: periodYear,
                  });
                }}
                disabled={!periodLabel || createPeriodMutation.isPending}
              >
                {createPeriodMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  t('calculator.create_new_period')
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

